import { ResourceLanguage } from 'i18next';
import { DisplayMode } from '@/types/display-mode.enum';
import { TopAssetTypesMetric } from '@/modules/dashboards/components/widgets/top-assets-widget/TopAssetTypesWidgetConfiguration';
import { TopLocationsMetric } from '@/modules/dashboards/components/widgets/top-locations-widget/TopLocationsWidgetConfiguration';
import { GroupPerTimeType } from '@/modules/dashboards/components/widgets/events-over-time-widget/group-per-time-type.enum';
import { EventSourceType, EventType, GpsSourceType } from '@/modules/events/api/events/event.contracts';

export const esTranslations: ResourceLanguage = {
  translation: {
    applicationLog: {
      title: 'Registro de Aplicación',
      messageId: 'ID de Mensaje',
      logItem_one: 'Registro',
      logItem_other: 'Registros',
      occurredAt: 'Ocurrido el',
      message: 'Mensaje',

      severity: {
        title: 'Gravedad',
        error: 'Error',
        warning: 'Advertencia',
        info: 'Información',
        all: 'Todas las gravedades',
      },
      category: {
        title: 'Categoría',
        import: 'Importación',
        eventProcessing: 'Procesamiento de Eventos',
        other: 'Otro',
        all: 'Todas las categorías',
      },
    },
    balance_block: {
      title: 'Balances',
      hierarchical: 'Jerárquico',
      individual: 'Individual',
      total_balance: 'Balance Total',
      total_balance_helper_text: 'Balance en esta ubicación: {{balance}}, Todas las sublocaciones: {{children}}',
      incoming: 'Entrante',
      incoming_helper_text: 'Entrante en esta ubicación: {{balance}}, Todas las sublocaciones: {{children}}',
      outgoing: 'Saliente',
      outgoing_helper_text: 'Saliente en esta ubicación: {{balance}}, Todas las sublocaciones: {{children}}',
      return_rate: 'Tasa de Retorno',
      return_rate_tooltip: '((Saliente - Saliente excluido ubicaciones de retorno) / Entrante) * 100',
      staytime: 'Tiempo de Estancia',
      staytime_helper_text: 'Activos inactivos en esta ubicación: {{balance}}, Todas las sublocaciones: {{children}}',
      [DisplayMode.HIERARCHICAL]: 'Jerárquico',
      [DisplayMode.INDIVIDUAL]: 'Individual',
      location_balance_helper_text: 'Esta fila muestra el total sumado del balance de esta ubicación + todas sus {{count}} sublocaciones',
    },
    balances: 'Balances',
    balance: 'Balance',
    buildup: 'Composición',
    counterpart_location: 'Ubicación Contraparte',
    view_buildup: 'Ver Composición',
    debit: 'Débito',
    credit: 'Crédito',
    hierarchical: 'Jerárquico',
    individual: 'Individual',
    total_balance: 'Balance Total',
    total_balance_helper_text: 'Balance en esta ubicación: {{balance}}, Todas las sublocaciones: {{children}}',
    incoming: 'Entrante',
    incoming_helper_text: 'Entrante en esta ubicación: {{balance}}, Todas las sublocaciones: {{children}}',
    outgoing: 'Saliente',
    outgoing_helper_text: 'Saliente en esta ubicación: {{balance}}, Todas las sublocaciones: {{children}}',
    return_rate: 'Tasa de Retorno',
    return_rate_tooltip: '((Saliente - Saliente excluido ubicaciones de retorno) / Entrante) * 100',
    staytime: 'Tiempo de Estancia',
    staytime_helper_text: 'Activos inactivos en esta ubicación: {{balance}}, Todas las sublocaciones: {{children}}',
    [DisplayMode.HIERARCHICAL]: 'Jerárquico',
    [DisplayMode.INDIVIDUAL]: 'Individual',
    location_balance_helper_text: 'Esta fila muestra el total sumado del balance de esta ubicación + todas sus {{count}} sublocaciones',
    dashboard: {
      top_asset_types_metric: {
        [TopAssetTypesMetric.assetCount]: 'Cantidad de Activos',
        [TopAssetTypesMetric.assetTotalMonetaryValue]: 'Valor Monetario Total de Activos',
      },
      top_locations_metric: {
        [TopLocationsMetric.assetCount]: 'Cantidad de Activos',
        [TopLocationsMetric.assetTotalMonetaryValue]: 'Valor Monetario Total de Activos',
      },
      group_per_time_type: {
        [GroupPerTimeType.DAY]: 'Día',
        [GroupPerTimeType.HOUR]: 'Hora',
        [GroupPerTimeType.WEEK]: 'Semana',
      },
      balance_over_time_period: {
        last_30_days: 'Últimos 30 días',
        last_90_days: 'Últimos 90 días',
      },
      dashboard: 'Panel de Control',
      home: 'Inicio',
      admin: 'Administración',
      create: {
        title: 'Crear un nuevo Panel de Control',
        description: 'Ingrese el nombre del Panel de Control que desea crear:',
        nameLabel: 'Nombre',
        helperText: 'Al menos 3 caracteres',
        cancel: 'Cancelar',
        createButton: 'Crear',
        success: '¡Panel de Control creado!',
      },
      rename: {
        title: 'Renombrar Panel de Control',
        description: 'Ingrese el nuevo nombre para el Panel de Control:',
        newNameLabel: 'Nuevo Nombre',
        helperText: 'Al menos 3 caracteres',
        cancel: 'Cancelar',
        renameButton: 'Renombrar',
        success: '¡Panel de Control renombrado!',
      },
      widget: {
        deleteButton: 'Eliminar Widget',
        editWidget: "Editar Widget '{{widgetName}}'",
        closeAriaLabel: 'cerrar',
        editConfiguration: 'Edite la configuración de este widget a continuación.',
      },
      widgetCatalog: {
        addWidgets: 'Agregar Widgets',
        closeAriaLabel: 'cerrar',
        dragDropInstruction: 'Arrastre y suelte un widget de la lista a continuación en el panel para comenzar a usarlo.',
      },
      balanceOverTimeWidget: {
        noBalancesFound: 'No se encontraron balances.',
        title: 'Balance de Ubicación a lo Largo del Tiempo',
        description: 'Muestra los Balances de una Ubicación a lo largo del tiempo, agrupados por un período específico.',
      },
      balanceOverTimeWidgetConfiguration: {
        title: 'Balance por día',
      },
      weeklyBalanceWidget: {
        title: 'Balance Semanal',
        description: 'Muestra el balance semanal, entrante y saliente para una ubicación y tipo de activo específicos.',
      },
      weeklyBalanceWidgetConfiguration: {
        title: 'Balance semanal',
      },
      countWidget: {
        title: 'Recuento de Activos',
        description: 'Muestra el recuento de Activos basado en filtros de Ubicación o Etiqueta.',
      },
      countWidgetConfiguration: {
        title: 'Total de Activos',
      },
      averageCycleCountWidget: {
        title: 'Recuento medio de ciclos',
        description: 'Muestra el recuento medio de ciclos de los Activos para un Tipo de Activo y punto de inicio del ciclo.',
        notConfigured: 'Seleccione un tipo de activo y una ubicación de inicio del ciclo para mostrar datos.',
      },
      averageCycleCountWidgetConfiguration: {
        title: 'Recuento medio de ciclos',
        cycleStart: 'Punto de inicio del ciclo según:',
        locationOption: 'Ubicación',
        labelOption: 'Etiqueta',
        filters: 'Filtros',
        currentLocation: 'Ubicación actual según:',
        startDate: 'Fecha de inicio',
        endDate: 'Fecha de fin',
      },
      eventsOverTimeByTypeWidget: {
        title: 'Eventos a lo Largo del Tiempo por Tipo',
        description: 'Muestra la cantidad total de Eventos de los últimos 7 días, agrupados por Tipo.',
      },
      eventsOverTimeByTypeWidgetConfiguration: {
        title: 'Eventos a lo Largo del Tiempo por Tipo',
      },
      eventsOverTimeWidget: {
        title: 'Eventos a lo Largo del Tiempo',
        description: 'Muestra la cantidad total de Eventos, agrupados por un período específico.',
      },
      eventsOverTimeWidgetConfiguration: {
        title: 'Eventos por día',
      },
      mapWidget: {
        title: 'Mapa',
        description: 'Muestra la posición de sus Activos en el mapa.',
      },
      mapWidgetConfiguration: {
        title: 'Mapa',
      },
      systemTotalWidget: {
        title: 'Total',
        description: 'Muestra la cantidad total de Activos, Rastreadores, Eventos o Ubicaciones en el sistema.',
        assets: 'Activos',
        locations: 'Ubicaciones',
        events: 'Eventos',
      },
      systemTotalWidgetConfiguration: {
        title: 'Total',
      },
      topAssetTypesWidget: {
        title: 'Tipos de Activos Principales',
        description: 'Un Top 10 de Tipos de Activos basado en una métrica que seleccione.',
      },
      topAssetTypesWidgetConfiguration: {
        title: 'Tipos de Activos Principales',
      },
      topLocationsWidget: {
        title: 'Ubicaciones Principales',
        description: 'Un Top 10 de Ubicaciones basado en una métrica que seleccione.',
      },
      topLocationsWidgetConfiguration: {
        title: 'Ubicaciones Principales por Cantidad de Activos',
      },
      deleteConfirmation: {
        questionText: '¿Está seguro de que desea eliminar este panel de control?',
        questionTitle: 'Eliminar Panel de Control',
      },
      header: 'Paneles de Control',
      addTab: '+ Panel de Control',
      actions: {
        showFullscreen: 'Mostrar en Pantalla Completa',
        editWidgets: 'Editar Widgets',
        deleteDashboard: 'Eliminar Panel de Control',
        renameDashboard: 'Renombrar Panel de Control',
        saveDashboard: 'Guardar Panel de Control',
      },
    },

    event: {
      title: 'Eventos',
      all: 'Todos',
      handheld: 'Portátil',
      event_one: 'Evento',
      event_other: 'Eventos',
      occurred_at: 'Ocurrido el',
      source: 'Origen',
      coordinates_source: 'Origen de Coordenadas',
      coordinates_accuracy: 'Precisión de Coordenadas',
      asset_code: 'Código de Activo',
      tracker_type: 'Tipo de Rastreador',
      location: 'Ubicación',
      tracker: 'Rastreador',
      device_name: 'Nombre del Dispositivo',
      rfids: 'RFIDs',
      barcodes: 'Códigos de Barras',
      mutations: 'Mutaciones',
      assets: 'Activos',
      trackers: 'Rastreadores',

      type: {
        title: 'Tipo',
        [EventType.Observation]: 'Observación',
        [EventType.Incoming]: 'Entrante',
        [EventType.Outgoing]: 'Saliente',
        [EventType.Inventory]: 'Inventario',
        [EventType.ADMINISTRATIVE]: 'Administrativo',
        [EventType.Heartbeat]: 'Latido',
        undefined: 'Desconocido',
        all: 'Todos los Tipos de Eventos',
      },

      gps_source: {
        [GpsSourceType.GPS]: 'GPS',
        [GpsSourceType.SIGFOX_ATLAS_NATIVE]: 'Triangulación Sigfox',
        [GpsSourceType.CERTIFIED_LOCATION_WIFI]: 'WiFi Certificado',
        [GpsSourceType.WIFI]: 'WiFi General',
        [GpsSourceType.CERTIFIED_LOCATION_BLE]: 'BLE Certificado',
        [GpsSourceType.CELLULAR]: 'Triangulación Celular',
        [GpsSourceType.LOCATION_CODE]: 'Código de Ubicación',
        undefined: 'Desconocido',
        null: '',
      },

      errors: {
        date_from_after: 'La fecha desde no puede ser posterior a la fecha hasta',
        date_to_before: 'La fecha hasta no puede ser anterior a la fecha desde',
        date_range_max_6_months: 'El rango de fechas no puede exceder los 6 meses',
      },
    },
    importer: {
      upload_a_file: 'Cargar un Archivo',
      import_error: {
        NON_UNIQUE_ASSET_CODE: 'CÓDIGO_DE_ACTIVO_NO_ÚNICO',
        NON_UNIQUE_TRACKER_BARCODE: 'CÓDIGO_DE_BARRAS_RASTREADOR_NO_ÚNICO',
        NON_UNIQUE_TRACKER_EPC: 'EPC_RASTREADOR_NO_ÚNICO',
        NON_UNIQUE_TRACKER_DEVICE_ID: 'ID_DISPOSITIVO_RASTREADOR_NO_ÚNICO',
        NON_EXISTENT_ASSET_TYPE_CODE: 'El Tipo de Activo No Existe',
        BARCODE_AND_EPC_CODE_AND_SIGFOX_ID_NOT_DEFINED: 'CÓDIGO_DE_BARRAS_Y_CÓDIGO_EPC_Y_ID_SIGFOX_NO_DEFINIDOS',
        NON_EXISTENT_LOCATION_CODE: 'La Ubicación No Existe',
      },
      next: 'Siguiente',
      previous: 'Anterior',
      preview: {
        preview: 'Vista Previa',
        file_contains_errors:
          'El archivo contiene {{count}} errores que deben resolverse. Una vez resueltos, intente cargar el archivo nuevamente.',
        file_upload_failed: 'Falló la carga del archivo. Por favor, inténtelo de nuevo.',
      },
      upload: {
        file_details: 'Detalles del Archivo',
        files_you_can_imported: 'Seleccione un archivo CSV para importar.',
        file_size: 'Tamaño',
        select_file: 'Seleccionar archivo',
        select_csv_file: 'Seleccionar archivo CSV',
        change_file: 'Cambiar Archivo',
      },
      import: {
        import: 'Importar',
      },
      asset_import: {
        title: 'Activos',
        description: 'Importar Activos, incluyendo su código de barras, código EPC o ID de dispositivo Sigfox.',
      },
      location_import: {
        title: 'Ubicaciones',
        description: 'Importar Ubicaciones',
      },
      parent_location_import: {
        title: 'Ubicaciones Principales',
        description: 'Establecer Ubicaciones Principales',
      },
    },

    assets_map: 'Mapa de Activos',
    legend: 'Leyenda',
    legend_start: 'Inicio',
    legend_end: 'Fin',
    legend_known_location: 'Ubicación Conocida',
    legend_intransit_location: 'En Tránsito',
    in_transit: 'En Tránsito',
    on_location: 'En Ubicación',
    setting: {
      api: {
        label: 'API',
        title: 'Configuración de API',
        general_description: 'Configuración común de API',
        url_title: 'URL de API',
        url_description: 'La URL de API pública de Posito que puede usar para integraciones.',
        documentation_title: 'Documentación de API',
        documentation_description: 'La Documentación Open API de la API.',
        keys_title: 'Claves de API',
        keys_description: 'Crear y administrar las claves de API que permiten el acceso a la API pública.',
        manage_keys_title: 'Administrar Claves de API',
        manage_keys_description: 'Puede crear, renombrar, habilitar/deshabilitar y eliminar Claves de API a continuación.',
        add: 'Agregar Clave de API',
        add_title: 'Agregar Clave de API',
        add_name_label: 'Nombre',
        add_description: 'Ingrese un nombre para la nueva Clave de API',
        add_success: 'Clave de API agregada exitosamente',
        add_name_validation: 'El nombre debe tener entre 1 y 25 caracteres',
        update_success: 'Clave de API actualizada exitosamente',
        delete: 'Eliminar Clave de API',
        delete_confirmation: '¿Está seguro de que desea eliminar esta Clave de API?',
        delete_success: 'Clave de API eliminada exitosamente',
        name: 'Nombre',
        created: 'Creado',
        key: 'Clave',
        active: 'Activo',
      },
      connector: {
        title: 'Configuración de Conectores',
        unaconnect_title: 'UnaConnect',
        unaconnect_description: 'Un Portal que puede enviar Eventos de Rastreador a Posito.',
        unaconnect_url_title: 'URL de Webhook de Sigfox',
        unaconnect_url_description: 'La URL para enviar eventos de Sigfox. Configure esto en UnaConnect.',
        nedap_title: 'Nedap Harmony',
        nedap_description: 'Un Portal que puede enviar Eventos de Rastreador a Posito.',
        nedap_url_title: 'URL de Webhook de Nedap Harmony',
        nedap_url_description: 'La URL para enviar eventos de Nedap Harmony. Configure esto en un Hook de Nedap Harmony.',
        epc_title: 'Eventos EPC',
        epc_description: 'Un endpoint para enviar eventos EPC.',
        epc_url_title: 'URL de Webhook EPC',
        epc_url_description: 'La URL para enviar eventos EPC. Configure esto en su software personalizado.',
        ble_gateway_title: 'Eventos de Gateway BLE',
        ble_gateway_description: 'Un endpoint para enviar eventos de Gateway BLE.',
        ble_gateway_url_title: 'URL de Webhook de Gateway BLE',
        ble_gateway_url_description: 'La URL para enviar eventos de Gateway BLE. Configure esto en su software de gateway BLE.',
        fixed_rfid_reader_title: 'Eventos de Lector RFID Fijo',
        fixed_rfid_reader_description: 'Un endpoint para enviar eventos de Lector RFID Fijo.',
        fixed_rfid_reader_url_title: 'URL de Webhook de Lector RFID Fijo',
        fixed_rfid_reader_url_description:
          'La URL para enviar eventos de Lector RFID Fijo. Configure esto en su software de lector RFID fijo.',
        barra_title: 'Eventos de Barra',
        barra_description: 'Un endpoint para enviar eventos de rastreador Barra.',
        barra_url_title: 'URL de Webhook de Barra',
        barra_url_description: 'La URL para enviar eventos de Barra. Configure esto en su software de Barra.',
      },
      event: {
        title: 'Configuración de Eventos',
        creation_title: 'Creación de Eventos',
        creation_description: 'Configure cómo se crean los Eventos.',
        auto_create_trackers_title: 'Crear Rastreadores automáticamente',
        auto_create_trackers_description:
          'Cuando se observa un Rastreador (escaneado/ingreso manual) que no coincide con ningún identificador de los Rastreadores actuales en el sistema, se creará un nuevo Rastreador con el identificador observado.',
        update_success: 'Configuración actualizada: Crear Rastreadores automáticamente',
      },
    },
    trackers: 'Rastreadores',
    tracker_add: 'Agregar Rastreador',
    tracker_new: 'Nuevo Rastreador',
    tracker_device_identifier: 'Identificador de Dispositivo',
    tracker_unique_identifier: 'Identificador Único',
    tracker: 'Rastreador',
    trackers_one: 'Rastreador',
    trackers_other: 'Rastreadores',
    tracker_unknown: 'Rastreador Desconocido',
    tracker_created: 'Rastreador creado',
    tracker_updated: 'Rastreador actualizado',
    tracker_deleted: 'Rastreador eliminado',
    tracker_delete_confirm: '¿Está seguro de que desea eliminar este rastreador?',
    error_creating_tracker: 'Error al Crear Rastreador',
    error_updating_tracker: 'Error al Actualizar Rastreador',
    error_deleting_tracker: 'Error al Eliminar Rastreador',
    error_duplicate_code: 'El código ya está en uso y debe ser único. Por favor, proporcione uno diferente.',
    error_no_permission_to_edit_trackers: 'No tiene permiso para editar rastreadores',
    tracker_in_use_barcode: 'El Código de Barras ya está en uso y debe ser único. Por favor, proporcione uno diferente.',
    tracker_in_use_epc: 'El EPC ya está en uso y debe ser único. Por favor, proporcione uno diferente.',
    tracker_in_use_device_id: 'El ID de Dispositivo ya está en uso y debe ser único. Por favor, proporcione uno diferente.',
    tracker_linked_to_other_asset:
      'Este Rastreador está actualmente vinculado a: Activo con código {{assetCode}}. Guardar este Activo lo desvinculará del Activo con código {{assetCode}} y lo vinculará a este Activo.',
    no_trackers_added: 'Aún no se han agregado Rastreadores.',
    barcode: 'Código de Barras',
    rfid: 'RFID',
    sigfox: 'Sigfox',
    ble: 'BLE',
    lte: 'LTE',
    assets: 'Activos',
    sign_in: 'Iniciar Sesión',
    search: 'Buscar',
    filter: 'Filtrar',
    filters: 'Filtros',
    unlink: 'Desvincular',
    delete: 'Eliminar',
    remove: 'Eliminar',
    save: 'Guardar',
    no_data: 'Sin datos',
    image_upload: 'Cargar Imagen',
    image_upload_new: 'Cargar Nueva Imagen',
    image_remove: 'Eliminar Imagen',
    showing_first: 'Mostrando los primeros {{amount}}',
    details: 'Detalles',
    unknown: 'Desconocido',
    at: 'en',
    total: 'Total',
    totals: 'Totales',
    code: 'Código',
    external_id: 'ID Externo',
    type: 'Tipo',
    identifier: 'Identificador',
    name: 'Nombre',
    overview: 'Vista General',
    export_excel: 'Exportar a Excel',
    columns: 'Columnas',
    refresh: 'Actualizar',
    select_all: 'Seleccionar Todo',
    deselect_all: 'Deseleccionar Todo',
    occurred_at: 'Ocurrido el',
    last_seen: 'Visto por última vez',
    online_since: 'En línea desde',
    offline_since: 'Fuera de línea desde',
    general: 'General',
    error_loading_data: 'Error al cargar datos',
    error_loading_data_verbose: 'Hubo un error al cargar los datos.',
    error_occurred: 'Ocurrió un error',
    cancel: 'Cancelar',
    copied: '¡Copiado!',
    copied_to_clipboard: 'Copiado al portapapeles',
    copy_to_clipboard: 'Copiar al portapapeles',
    copy_transaction_id: 'Copiar ID de transacción al portapapeles',
    or: 'O',
    events: 'Eventos',
    transactions: 'Transacciones',
    transaction: 'Transacción',
    create_transaction: 'Crear Transacción',
    transaction_created: 'Transacción creada exitosamente',
    transaction_updated: 'Transacción actualizada exitosamente',
    transaction_status_updated: 'Estado de transacción actualizado exitosamente',
    bulk_transaction_status_updated: '{{count}} estados de transacción actualizados exitosamente',
    bulk_update_transaction_status: 'Actualización Masiva de Estado de Transacción',
    bulk_update_status: 'Actualización Masiva de Estado',
    bulk_update_status_description:
      'Está a punto de actualizar el estado de {{count}} transacción(es). Seleccione el nuevo estado a continuación.',
    finalized_transactions_not_updated: 'Nota: Las transacciones finalizadas en su selección no se actualizarán.',
    update_status: 'Actualizar Estado',
    updating: 'Actualizando...',
    transaction_status_helper_text: 'Actualizar el estado de la transacción',
    error_creating_transaction: 'Error al crear transacción',
    error_updating_transaction: 'Error al actualizar transacción',
    error_updating_transaction_status: 'Error al actualizar estado de transacción',
    error_loading_transaction: 'Error al cargar transacción',
    cannot_update_finalized_transaction: 'No se puede actualizar una transacción finalizada',
    view: 'Ver',
    transaction_id: 'ID de Transacción',
    transaction_details: 'Detalles de Transacción',
    transaction_details_placeholder: 'Los detalles de la transacción se administrarán aquí',
    created_at: 'Creado el',
    created_by: 'Creado por',
    carrier: 'Transportista',
    document_number: 'Número de Documento',
    reference_number: 'Número de Referencia',
    notes: 'Notas',
    transfer: 'Transferencia',
    correction: 'Corrección',
    system: 'Sistema',
    in_progress: 'En Progreso',
    finalized: 'Finalizado',
    source: 'Fuente',
    manual: 'Manual',
    automatic: 'Automático',
    finalize: 'Finalizar',
    additional_information: 'Información Adicional',
    additional_details: 'Detalles Adicionales',
    date: 'Fecha',
    select_date: 'Seleccionar fecha',
    time: 'Hora',
    add_time: 'Agregar hora',
    edit_time: 'Editar hora',
    asset_type: 'Tipo de Activo',
    add_asset: 'Agregar Activo',
    quantity: 'Cantidad',
    save_and_create_new: 'Guardar y Crear Nuevo',
    unsaved_changes: 'Cambios sin Guardar',
    unsaved_changes_message: 'Tiene cambios sin guardar. ¿Está seguro de que desea descartarlos?',
    confirm: 'Confirmar',
    create: 'Crear',
    clear_selection: 'Limpiar Selección',
    toggle_dark_mode: 'Alternar Modo Oscuro',
    dark_mode: 'Modo Oscuro',
    configuration: 'Configuración',
    insights: 'Información',
    dashboards: 'Paneles de Control',
    map: 'Mapa',
    operations: 'Operaciones',
    setup: 'Configuración',
    locations: 'Ubicaciones',
    scan_a_barcode: 'Escanear un Código de Barras',
    position_the_barcode_in_the_center_of_the_screen: 'Coloque el código de barras en el centro de la pantalla.',
    media_devices_not_supported: 'Los Dispositivos Multimedia no son compatibles con este navegador.',
    barcode_detector_not_supported: 'El Detector de Códigos de Barras no es compatible con este navegador.',
    create_new_or_use_existing: 'Crear Nuevo o Usar Existente',
    create_new: 'Crear Nuevo',
    use_existing: 'Usar Existente',
    link: 'Vincular',
    link_a_tracker_to_an_asset: 'Vincular un Rastreador a un Activo',
    select_asset_type_and_create_new_or_connect_existing:
      'Seleccione un Tipo de Activo y cree un nuevo Activo y/o Rastreador, o conecte los existentes.',
    asset_code: 'Código de Activo',
    existing_trackers: 'Rastreadores Existentes',
    no_existing_trackers: 'No hay rastreadores existentes',
    add_trackers: 'Agregar Rastreadores',
    asset_not_found: 'Activo no encontrado',
    error_fetching_existing_asset: 'Error al obtener el Activo existente',
    no_asset_selected: 'No se seleccionó ningún Activo',
    no_asset_type_selected: 'No se seleccionó ningún Tipo de Activo',
    no_asset_code_entered: 'No se ingresó ningún Código de Activo',
    no_tracker_identifier_entered: 'No se ingresó ningún Identificador de Rastreador',
    error_fetching_existing_trackers: 'Error al vincular Activo al Rastreador, no se pudieron obtener los Rastreadores existentes',
    barcode_tracker_already_linked:
      'El Rastreador de Código de Barras ya está vinculado a un Activo, desvincúlelo primero en el sitio de escritorio',
    rfid_tracker_already_linked: 'El Rastreador RFID ya está vinculado a un Activo, desvincúlelo primero en el sitio de escritorio',
    sigfox_tracker_already_linked: 'El Rastreador Sigfox ya está vinculado a un Activo, desvincúlelo primero en el sitio de escritorio',
    ble_tracker_already_linked: 'El Rastreador BLE ya está vinculado a un Activo, desvincúlelo primero en el sitio de escritorio',
    lte_tracker_already_linked: 'El Rastreador LTE ya está vinculado a un Activo, desvincúlelo primero en el sitio de escritorio',
    asset_code_already_exists: 'Ya existe un activo con este código',
    error_fetching_existing_assets: 'Error al vincular Activo al Rastreador, no se pudieron obtener los Activos existentes',
    error_creating_barcode_tracker: 'Error al vincular Activo al Rastreador, no se pudo crear el nuevo Rastreador de Código de Barras',
    error_creating_rfid_tracker: 'Error al vincular Activo al Rastreador, no se pudo crear el nuevo Rastreador RFID',
    error_creating_sigfox_tracker: 'Error al vincular Activo al Rastreador, no se pudo crear el nuevo Rastreador Sigfox',
    error_creating_ble_tracker: 'Error al vincular Activo al Rastreador, no se pudo crear el nuevo Rastreador BLE',
    error_creating_lte_tracker: 'Error al vincular Activo al Rastreador, no se pudo crear el nuevo Rastreador LTE',
    no_trackers_created: 'Error al vincular Activo al Rastreador, no se pudieron crear Rastreadores',
    error_creating_new_asset: 'Error al vincular Activo al Rastreador, no se pudo crear un nuevo Activo',
    error_linking_tracker_to_asset: 'Error al vincular Activo al Rastreador, no se pudo vincular el Rastreador al Activo',
    error_linking_asset_to_tracker: 'Error al vincular Activo al Rastreador',
    asset_linked_to_trackers: 'Activo vinculado al/los Rastreador(es)',
    showing_most_recent_events: 'Mostrando los {{count}} Eventos más recientes.',
    first_event_date: 'Fecha del Primer Evento',
    last_event_date: 'Fecha del Último Evento',
    stay_time: 'Tiempo de Estancia',
    tracker_n: 'Rastreador {{number}}',
    no_gps_location_found: 'No se encontró ubicación GPS.',
    unarchive_asset_title: 'Desarchivar Activo',
    unarchive_asset_text:
      'El código de activo que está intentando editar ya se usa en un activo archivado, ¿desea desarchivar el activo en su lugar?',
    unarchive_asset_type_title: 'Desarchivar Tipo de Activo',
    unarchive_asset_type_text:
      'El código de tipo de activo que está intentando editar ya se usa en un tipo de activo archivado, ¿desea desarchivar el tipo de activo en su lugar?',
    asset_type_with_code_already_exists: 'Ya existe un tipo de activo con este código',

    unarchive_location_title: 'Desarchivar Ubicación',
    unarchive_location_text:
      'El código de ubicación que está intentando editar ya se usa en una ubicación archivada, ¿desea desarchivar la ubicación en su lugar?',
    location_with_code_already_exists: 'Ya existe una ubicación con este código',
    unarchive: 'Desarchivar',
    current_location: 'Ubicación Actual',
    link_a_tracker: 'Vincular un Rastreador',
    create_new_tracker: 'Crear nuevo Rastreador',
    delete_confirmation_question_one:
      "¿Está seguro de que desea eliminar este activo? Los rastreadores vinculados a este activo no se eliminarán, pero se desvincularán de este activo. Este activo se moverá a la ubicación del sistema 'Archivado'.",
    delete_confirmation_question_other:
      "¿Está seguro de que desea eliminar estos activos? Los rastreadores vinculados a estos activos no se eliminarán, pero se desvincularán de estos activos. Estos activos se moverán a la ubicación del sistema 'Archivado'.",
    error_deleting_asset: 'Error al eliminar Activo',
    asset_deleted: 'Activo eliminado',
    no_permission_edit_assets: 'No tiene permiso para editar activos',
    duplicate_code: 'El código ya está en uso y debe ser único. Por favor, proporcione uno diferente.',
    error_creating_asset: 'Error al crear Activo',
    error_updating_asset: 'Error al actualizar Activo',
    asset_created: 'Activo creado',
    asset_updated: 'Activo actualizado',
    linking_trackers_warning:
      'El Activo fue creado, pero algo salió mal al vincular los Rastreadores. Por favor, verifique el Activo para ver si los Rastreadores correctos están vinculados.',
    new_asset: 'Nuevo Activo',
    asset_with_code: 'Activo - {{code}}',
    history: 'Historial',
    tablist_aria_label: 'Pestañas de Detalle de Activo',
    minimum_stay_time_days: 'Tiempo Mínimo de Estancia (días)',
    move_assets: 'Mover Activos',
    assets_moved_successfully: 'Activos movidos exitosamente',
    failed_to_move_assets: 'Error al mover activos',
    error_moving_assets: 'Error al mover Activos',
    move_assets_to_location_title: 'Mover {{count}} Activo(s) a Ubicación...',
    move: 'Mover',
    no_permission_to_edit_users: 'No tiene permiso para editar usuarios',
    error_creating_user: 'Error al Crear Usuario',
    user_email_already_exists: 'Esta dirección de correo electrónico ya está en uso',
    user_created: 'Usuario creado',
    error_updating_user: 'Error al Actualizar Usuario',
    user_updated: 'Usuario actualizado',
    no_data_error: 'Error sin datos',
    error_deleting_user: 'Error al eliminar Usuario',
    user_deleted: 'Usuario eliminado',
    email: 'Correo Electrónico',
    password: 'Contraseña',
    password_helper_text: 'Ingrese una Contraseña para cambiarla, déjela en blanco para no cambiarla.',
    confirm_delete_user: '¿Está seguro de que desea eliminar este Usuario?',
    this_field_has_to_be_filled: 'Este campo debe completarse.',
    this_is_not_a_valid_email: 'Este no es un correo electrónico válido.',
    you_must_select_a_role: 'Debe seleccionar un rol.',
    password_with_minimum_characters_required: 'Se requiere una contraseña con al menos 6 caracteres',
    user_avatar: 'Avatar de {{name}}',
    user_type: 'Tipo de Usuario',
    user_type_helper_text: 'Seleccione el tipo de usuario. Los Operadores tienen acceso completo, los Invitados tienen acceso limitado.',
    user_type_operator: 'Operador',
    user_type_guest: 'Invitado',
    user_type_guest_role: 'Visualizador (Automático)',
    user_type_guest_role_helper_text: 'Los usuarios Invitados se asignan automáticamente el rol de Visualizador con permisos limitados.',
    assigned_locations: 'Ubicaciones Asignadas',
    location_id: 'ID de Ubicación',
    remove_location: 'Eliminar ubicación',
    no_locations_assigned: 'No hay ubicaciones asignadas',
    add_locations: 'Agregar Ubicaciones',
    add_location: 'Agregar Ubicación →',
    search_locations_placeholder: 'Buscar ubicaciones...',
    added: 'Agregado',
    active: 'Activo',
    inactive: 'Inactivo',
    users: 'Usuarios',
    roles: 'Roles',
    new_user: 'Nuevo Usuario',
    user_name: 'Usuario - {{name}}',
    user_tabs: 'Pestañas de Usuario',
    add_user: 'Agregar Usuario',
    view_dashboards: 'Ver Paneles de Control',
    allows_user_to_view_dashboards: 'Permite a un Usuario ver Paneles de Control.',
    edit_dashboards: 'Editar Paneles de Control',
    allows_user_to_create_edit_delete_dashboards: 'Permite a un Usuario crear, editar y eliminar Paneles de Control.',
    view_map: 'Ver Mapa',
    allows_user_to_view_map: 'Permite a un Usuario ver el Mapa.',
    view_balances: 'Ver Balances',
    allows_user_to_view_balances: 'Permite a un Usuario ver Balances.',
    view_events: 'Ver Eventos',
    allows_user_to_view_events: 'Permite a un Usuario ver Eventos.',
    view_movements: 'Ver Movimientos',
    allows_user_to_view_movements: 'Permite a un Usuario ver Movimientos.',
    view_transactions: 'Ver Transacciones',
    allows_user_to_view_transactions: 'Permite a un Usuario ver Transacciones.',
    edit_transactions: 'Editar Transacciones',
    allows_user_to_create_edit_delete_transactions: 'Permite a un Usuario crear, editar y eliminar Transacciones.',
    allows_guest_to_create_edit_transactions_in_assigned_locations:
      'Permite a un Invitado crear y editar Transacciones en sus Ubicaciones asignadas.',
    view_locations: 'Ver Ubicaciones',
    allows_user_to_view_locations: 'Permite a un Usuario ver Ubicaciones.',
    edit_locations: 'Editar Ubicaciones',
    allows_user_to_create_edit_delete_locations: 'Permite a un Usuario crear, editar y eliminar Ubicaciones.',
    view_assets: 'Ver Activos',
    allows_user_to_view_assets: 'Permite a un Usuario ver Activos.',
    edit_assets: 'Editar Activos',
    allows_user_to_create_edit_delete_assets: 'Permite a un Usuario crear, editar y eliminar Activos.',
    asset_types: 'Tipos de Activos',
    view_asset_types: 'Ver Tipos de Activos',
    allows_user_to_view_asset_types: 'Permite a un Usuario ver Tipos de Activos.',
    edit_asset_types: 'Editar Tipos de Activos',
    allows_user_to_create_edit_delete_asset_types: 'Permite a un Usuario crear, editar y eliminar Tipos de Activos.',
    view_trackers: 'Ver Rastreadores',
    allows_user_to_view_trackers: 'Permite a un Usuario ver Rastreadores.',
    edit_trackers: 'Editar Rastreadores',
    allows_user_to_create_edit_delete_trackers: 'Permite a un Usuario crear, editar y eliminar Rastreadores.',
    devices: 'Dispositivos',
    view_devices: 'Ver Dispositivos',
    allows_user_to_view_devices: 'Permite a un Usuario ver Dispositivos.',
    edit_devices: 'Editar Dispositivos',
    allows_user_to_create_edit_delete_devices: 'Permite a un Usuario crear, editar y eliminar Dispositivos.',
    labels: 'Etiquetas',
    view_labels: 'Ver Etiquetas',
    allows_user_to_view_labels: 'Permite a un Usuario ver Etiquetas.',
    edit_labels: 'Editar Etiquetas',
    allows_user_to_create_edit_delete_labels: 'Permite a un Usuario crear, editar y eliminar Etiquetas.',
    event_settings: 'Configuración de Eventos',
    view_event_settings: 'Ver Configuración de Eventos',
    allows_user_to_view_event_settings: 'Permite a un Usuario ver la Configuración de Eventos.',
    edit_event_settings: 'Editar Configuración de Eventos',
    allows_user_to_create_edit_delete_event_settings: 'Permite a un Usuario crear, editar y eliminar la Configuración de Eventos.',
    connectors: 'Conectores',
    view_connectors: 'Ver Conectores',
    allows_user_to_view_connectors: 'Permite a un Usuario ver Conectores.',
    api_settings: 'Configuración de API',
    view_api_settings: 'Ver Configuración de API',
    allows_user_to_view_api_settings: 'Permite a un Usuario ver la Configuración de API.',
    edit_api_settings: 'Editar Configuración de API',
    allows_user_to_edit_api_settings: 'Permite a un Usuario editar la Configuración de API.',
    view_users: 'Ver Usuarios',
    allows_user_to_view_users: 'Permite a un Usuario ver Usuarios.',
    edit_users: 'Editar Usuarios',
    allows_user_to_edit_users: 'Permite a un Usuario editar Usuarios.',
    view_roles: 'Ver Roles',
    allows_user_to_view_roles: 'Permite a un Usuario ver Roles.',
    edit_roles: 'Editar Roles',
    allows_user_to_create_edit_delete_roles: 'Permite a un Usuario crear, editar y eliminar Roles.',
    file_importer: 'Importador de Archivos',
    use_file_importer: 'Usar Importador de Archivos',
    allows_user_to_use_file_importer: 'Permite a un Usuario usar el Importador de Archivos.',
    application_log: 'Registro de Aplicación',
    view_application_log: 'Ver Registro de Aplicación',
    allows_user_to_view_application_log: 'Permite a un Usuario ver el Registro de Aplicación.',
    mobile: 'Móvil',
    mobile_connect: 'Conectar Móvil',
    allows_user_to_connect_trackers_to_assets_in_smartphone_view:
      'Permite a un Usuario conectar Rastreadores a Activos en la vista de smartphone de Posito.',
    mobile_events: 'Eventos Móviles',
    allows_user_to_view_events_in_smartphone_view: 'Permite a un Usuario ver Eventos en la vista de smartphone de Posito.',
    actions: 'Acciones',
    enabled: 'Habilitado',
    permissions: 'Permisos',
    guest_permissions_description: 'Seleccione qué permisos debe tener este usuario invitado.',
    guest_transaction_edit_restriction: 'Como usuario invitado, solo puede editar transacciones que usted mismo haya creado.',
    read_only_role_cannot_be_edited:
      'Este Rol es de Solo Lectura y no se puede editar, para evitar que se bloquee a sí mismo fuera del sistema.',
    role: 'Rol',
    base_data: 'Datos Base',
    settings: 'Configuración',
    api: 'API',
    updated_setting: 'Configuración actualizada',
    error_updating_setting: 'Error al actualizar la configuración',
    updated_setting_auto_create_trackers: "Configuración actualizada: 'Crear Rastreadores automáticamente'",
    download_template: 'Descargar Plantilla',
    start_import: 'Iniciar Importación',
    import_ready_message:
      'Ahora está listo para importar los datos a Posito. Haga clic en el botón a continuación para iniciar el proceso de importación.',
    importing_data: 'Importando datos...',
    import_successful: 'Importación exitosa',
    import_failed: 'Importación fallida',
    uploading_file: 'Cargando archivo...',
    import: 'Importar',
    asset_import: 'Importación de Activos',
    import_assets_and_associate_them_with_trackers: 'Importar activos y asociarlos con rastreadores.',
    assets_to_create: 'Activos a Crear',
    trackers_to_create: 'Rastreadores a Crear',
    valid: 'Válido',
    invalid: 'Inválido',
    error_reason: 'Razón del Error',
    missing_asset_code: 'Código de activo faltante',
    invalid_asset_type: 'Tipo de activo inválido',
    unknown_error: 'Error desconocido',
    non_unique_asset_code: 'CÓDIGO_DE_ACTIVO_NO_ÚNICO',
    non_unique_tracker_barcode: 'CÓDIGO_DE_BARRAS_RASTREADOR_NO_ÚNICO',
    non_unique_tracker_epc: 'EPC_RASTREADOR_NO_ÚNICO',
    non_unique_tracker_device_id: 'ID_DISPOSITIVO_RASTREADOR_NO_ÚNICO',
    non_existent_asset_type_code: 'El Tipo de Activo No Existe',
    barcode_and_epc_code_and_sigfox_id_not_defined: 'CÓDIGO_DE_BARRAS_Y_CÓDIGO_EPC_Y_ID_SIGFOX_NO_DEFINIDOS',
    non_existent_location_code: 'La Ubicación No Existe',
    location_import: 'Importación de Ubicaciones',
    import_locations_into_the_system: 'Importar ubicaciones al sistema.',
    locations_to_create: 'Ubicaciones a Crear',
    location_code: 'Código de Ubicación',
    location_name: 'Nombre de Ubicación',
    missing_location_code: 'Código de ubicación faltante',
    missing_location_name: 'Nombre de ubicación faltante',
    duplicate_location_code: 'Código de ubicación duplicado',
    parent_location_import: 'Importación de Ubicaciones Principales',
    import_parent_locations: 'Importar ubicaciones principales.',
    number_locations_to_assign_new_parent: 'Número de Ubicaciones para asignar (nueva) Principal',
    parent_location_code: 'Código de Ubicación Principal',
    child_location_code: 'Código de Ubicación Secundaria',
    missing_parent_location_code: 'Código de ubicación principal faltante',
    missing_child_location_code: 'Código de ubicación secundaria faltante',
    duplicate_parent_location_code: 'Código de ubicación principal duplicado',
    no_events_found: 'No se encontraron eventos',
    for: 'Para',
    asset: 'Activo',
    asset_one: 'Activo',
    asset_other: 'Activos',
    move_assets_to_another_type: 'Mover Activos a Otro Tipo',
    asset_type_cannot_be_deleted:
      'El Tipo de Activo "{{assetTypeName}}" no se puede eliminar porque tiene Activos asociados. Por favor, seleccione otro Tipo de Activo para mover estos Activos.',
    select_new_asset_type: 'Seleccionar Nuevo Tipo de Activo',
    failed_to_move_assets_try_again:
      'Error al mover Activos. Por favor, inténtelo nuevamente, o contacte al soporte si el problema persiste.',
    add_asset_type: 'Agregar Tipo de Activo',
    image: 'Imagen',
    description: 'Descripción',
    model_year: 'Año del Modelo',
    width_mm: 'Ancho (mm)',
    height_mm: 'Alto (mm)',
    depth_mm: 'Profundidad (mm)',
    volume_cubic_mm: 'Volumen (mm cúbicos)',
    product_capacity: 'Capacidad del Producto',
    monetary_value: 'Valor Monetario',
    total_monetary_value: 'Valor Monetario Total',
    total_product_capacity: 'Capacidad Total del Producto',
    number_of_assets: 'N° de Activos',
    new_asset_type: 'Nuevo Tipo de Activo',
    current_balance: 'Balance Actual',
    error_creating_asset_type: 'Error al crear tipo de activo',
    error_updating_asset_type: 'Error al actualizar tipo de activo',
    error_uploading_image: 'Error al cargar imagen',
    error_removing_image: 'Error al eliminar imagen',
    asset_type_created: 'Tipo de activo creado',
    asset_type_updated: 'Tipo de activo actualizado',
    no_permission_edit_asset_types: 'No tiene permiso para editar tipos de activos',
    asset_type_deleted: 'Tipo de activo eliminado',
    delete_asset_type_confirmation: '¿Está seguro de que desea eliminar este tipo de activo?',
    error_asset_type_has_assets:
      'Este tipo de activo tiene activos y no se puede eliminar. Por favor, asigne un tipo de activo diferente a los activos actuales.',
    error_deleting_asset_type: 'Error al eliminar tipo de activo',
    asset_types_helper_text: 'Seleccione uno o más tipos de activos.',
    add: 'Agregar',
    asset_type_code: 'Código de Tipo de Activo',
    asset_type_name: 'Nombre de Tipo de Activo',
    from_location: 'Desde Ubicación',
    to_location: 'Hacia Ubicación',
    movement: 'Movimiento',
    movements: 'Movimientos',
    movement_one: 'Movimiento',
    movement_other: 'Movimientos',
    event_type: 'Tipo de Evento',
    event_source: 'Origen del Evento',
    location: 'Ubicación',
    assets_on_location: 'Activos en esta Ubicación',
    recent_events: 'Eventos Recientes',
    mobile_scanner: 'Escáner Móvil',
    ble_gateway: 'Gateway BLE',
    fixed_rfid_reader: 'Lector RFID Fijo',
    all: 'Todos',
    mobile_scanners: 'Escáneres Móviles',
    ble_gateways: 'Gateways BLE',
    fixed_rfid_readers: 'Lectores RFID Fijos',
    no_edit_device_permission: 'No tiene permiso para editar dispositivos',
    device_created: 'Dispositivo creado',
    device_updated: 'Dispositivo actualizado',
    error_creating_device: 'Error al crear dispositivo',
    error_updating_device: 'Error al actualizar dispositivo',
    device: 'Dispositivo',
    new_device: 'Nuevo Dispositivo',
    brand: 'Marca',
    serial_number: 'Número de Serie',
    add_device: 'Agregar Dispositivo',
    last_event_received_at: 'Último Evento Recibido el',
    code_can_be_used_in_posito_scan_app:
      'Este código se puede usar en la aplicación Posito Scan para vincular la aplicación a este Dispositivo.',
    battery_voltage: 'Voltaje de Batería',
    battery_voltage_updated_at: 'Voltaje de batería actualizado por última vez el {{date}}',
    battery_status: 'Estado de la Batería',
    battery_status_high: 'Alto',
    battery_status_medium: 'Medio',
    battery_status_low: 'Bajo',
    battery_status_unknown: 'Desconocido',
    battery_status_updated_at: '{{status}}, actualizado por última vez el {{date}}',
    tracker_type: 'Tipo de Rastreador',
    linked_trackers: 'Vinculados',
    unlinked_trackers: 'No Vinculados',
    state: 'Estado',
    sublocations: 'Sububicaciones',
    geofence: 'Geocerca',
    geofence_select_title: 'Seleccionar Dirección de Geocerca',
    geofence_select_description:
      'Estas son las ubicaciones que pudimos encontrar según la dirección de esta ubicación. Seleccione una para determinar la ubicación de la geocerca. Opcionalmente puede ajustar el radio de la geocerca.',
    geofence_radius: 'Radio de geocerca',
    location_select: 'Seleccionar una ubicación',
    location_select_description: 'Seleccione una ubicación para filtrar.',
    balance_over_time_for: "Balance a lo largo del tiempo para '{{locationName}}'",
    period: 'Período',
    select_up_to_5_asset_types: 'Seleccione hasta 5 Tipos de Activos para mostrar',
    delete_location_error_assets:
      'La ubicación tiene activos asignados. Muévalos a una ubicación diferente antes de eliminar esta ubicación.',
    delete_location_error_system: 'Esta es una ubicación del sistema, y las ubicaciones del sistema no se pueden eliminar.',
    delete_location_error_children:
      'La ubicación tiene sububicaciones. Elimínelas o reasígnelas a una ubicación principal diferente, antes de eliminar esta ubicación.',
    delete_location_error: 'Error al eliminar ubicación',
    delete_location_success: 'Ubicación eliminada',
    location_code_already_exists: 'El código ya está en uso y debe ser único. Por favor, proporcione uno diferente.',
    create_location_error: 'Error al crear ubicación',
    create_location_success: 'Ubicación creada',
    update_location_error: 'Error al actualizar ubicación',
    update_location_success: 'Ubicación actualizada',
    delete_location_confirmation:
      '¿Está seguro de que desea eliminar esta ubicación? Los eventos y movimientos vinculados a esta ubicación no se eliminarán, para mantener un historial completo de sus activos.',
    location_no_permission: 'No tiene permiso para editar ubicaciones',
    parent_location: 'Ubicación Principal',
    include_in_return_rate: 'Incluir en el cálculo de la tasa de retorno',
    include_in_return_rate_tooltip:
      'Decide si los activos que llegan a esta ubicación se incluirán en la tasa de retorno de la ubicación desde donde partió el activo',
    location_type: 'Tipo de ubicacion',
    location_type_carrier: 'Transportista',
    location_type_depo: 'Depósito',
    location_type_system: 'Sistema',
    address: 'Dirección',
    country: 'País',
    city: 'Ciudad',
    postal_code: 'Código Postal',
    street_name: 'Nombre de Calle',
    street_number: 'Número de Calle',
    new_location: 'Nueva Ubicación',
    system_location_alert: 'Esta ubicación es una ubicación del sistema y no se puede eliminar ni se puede cambiar el código.',
    system_location_geofence_readonly: 'La edición de geocerca está deshabilitada para ubicaciones del sistema.',
    system_location_tooltip:
      'Las Ubicaciones del Sistema son creadas por Posito y se utilizan para ciertas funcionalidades predefinidas. Posito usa el código de ubicación para identificar una ubicación del sistema y, por lo tanto, no se puede cambiar.',
    create_geofence_from_address: 'Crear geovalla desde una direccion',
    more_info: 'Más Información',
    location_one: 'Ubicación',
    location_other: 'Ubicaciones',
    start_balance: 'Balance Inicial',
    location_start_balances: 'Balances Iniciales de Ubicación',
    set_starting_balances: 'Establecer Balances Iniciales',
    set_starting_balances_description:
      'Agregue balances iniciales para tipos de activos en esta ubicación. Seleccione un tipo de activo, establezca la cantidad y elija una fecha de inicio. Si existen transacciones en esta ubicación, solo puede seleccionar una fecha de inicio anterior a la transacción existente.',
    save_balances: 'Guardar Balances',
    balances_saved_successfully: 'Balances guardados exitosamente',
    failed_to_save_some_balances: 'No se pudieron guardar algunos balances',
    error_saving_balances: 'Error al guardar balances',
    no_new_balances_to_save: 'No hay balances nuevos para guardar',
    error_loading_asset_types: 'Error al cargar tipos de activos',
    add_start_balance: 'Agregar Balance Inicial',
    add_another: 'Agregar Otro',
    no_start_balances_added_yet: 'Aún no se han agregado balances iniciales',
    start_balances_cannot_be_updated: 'Nota: Los balances iniciales existentes no se pueden actualizar ni eliminar.',
    please_fill_all_fields: 'Por favor complete todos los campos correctamente',
    start_date: 'Fecha de Inicio',
    start_date_cannot_be_after_first_transaction: 'La fecha de inicio no puede ser posterior a la fecha de la primera transacción ({{date}})',
    max_date: 'Fecha máxima: {{date}}',
    existing_start_balances: 'Balances Iniciales Existentes',
    new_start_balances: 'Nuevos Balances Iniciales',
    amount: 'Cantidad',
    set_labels: 'Establecer Etiquetas',
    discard_changes: 'Descartar cambios',
    label_fetch_error: 'Hubo un error al obtener las etiquetas. Por favor, inténtelo más tarde.',
    add_label: 'Agregar Etiqueta',
    select_label_value: 'Por favor seleccione un valor para la etiqueta',
    select_label: 'Por favor seleccione una Etiqueta',
    manage_label_assignments: 'Administrar Asignaciones de Etiquetas',
    manage_label_assignments_empty_state_title: 'No se encontraron etiquetas',
    create_label: 'Crear Etiqueta',
    no_labels_assigned: 'No hay etiquetas asignadas a la selección.',
    label_create_description: 'Puede crear y administrar etiquetas en la sección de configuración.',
    labels_description: 'Las etiquetas le ayudan a agrupar y categorizar sus ubicaciones.',
    create_a_label: 'Crear una Etiqueta',
    assign_label_to_location: 'y comience a asignarla a sus ubicaciones.',
    label_description_example: 'Las etiquetas ayudan a organizar sus recursos',
    assignments: 'Asignaciones',
    value: 'Valor',
    values: 'Valores',
    label_one: 'Etiqueta',
    label_other: 'Etiquetas',
    new_label: 'Nueva Etiqueta',
    label_created: 'Etiqueta creada',
    label_updated: 'Etiqueta actualizada',
    label_deleted: 'Etiqueta eliminada',
    label_name: 'Nombre de Etiqueta',
    color: 'Color',
    add_value: 'Agregar Valor',
    label_value_caption: 'Agregue uno o más valores de Etiqueta que puedan ayudar a categorizar Ubicaciones.',
    label_delete_confirmation: '¿Está seguro de que desea eliminar esta etiqueta?',
    label_values: 'Valores de Etiqueta',
    label_helper_text: 'Seleccione uno o más valores de etiqueta.',
    label_error_permission: 'No tiene permiso para editar etiquetas',
    label_error_duplicate: 'El nombre ya está en uso y debe ser único. Por favor, proporcione uno diferente.',
    label_error_creating: 'Error al crear Etiqueta',
    label_error_updating: 'Error al actualizar Etiqueta',
    label_error_deleting: 'Error al eliminar Etiqueta',
    label_error_values_unique: 'Los valores deben ser únicos',
    label_error_values_empty: 'Los valores no pueden estar vacíos',
    assets_total_elements: 'Activos ({{count}})',
    arrived: 'Llegó',
    pagination_result: '{{total}} {{entity}} encontrados',
    balance_per_asset_type: 'Balance por Tipo de Activo',
    arrived_at: 'Llegó el',
    arrived_at_date_from: 'Llegó Desde',
    arrived_at_date_to: 'Llegó Hasta',
    export_to_excel: 'Exportar a Excel',
    close: 'Cerrar',
    error_loading_assets: 'Error al cargar activos',
    assets_at_location: 'Activos en la ubicación',
    model: 'Modelo',
    iccid: 'ICCID',
    enums: {
      event_source: {
        [EventSourceType.GATE]: 'Puerta',
        [EventSourceType.MANUAL]: 'Manual',
        [EventSourceType.TRACKER]: 'Rastreador',
        [EventSourceType.IMPORTER]: 'Importador',
        [EventSourceType.HANDHELD]: 'Portátil',
        [EventSourceType.BLE_GATEWAY]: 'Gateway BLE',
        [EventSourceType.FIXED_RFID_READER]: 'Lector RFID Fijo',
        [EventSourceType.AUTOMATION]: 'Automatización',
        undefined: 'Desconocido',
      } as Record<EventSourceType, string>,
    },
    high: 'Alto',
    medium: 'Medio',
    low: 'Bajo',
    location_entered_at: 'Ubicación ingresada el {{date}}',
    number_of_days: '{{day}} días',
    no_permission_to_view_page: 'No tiene permiso para ver esta página',
    week_short: 'Sem {{week}}',
    error_loading_settings: 'Error al cargar configuración',
    error_loading_user: 'Error al cargar usuario',
    error_loading_user_settings: 'Error al cargar configuración de usuario',
    account_disabled_contact_administrator: 'Su cuenta ha sido deshabilitada. Por favor, contacte a su administrador.',
    profile: 'Perfil',
    environment: 'Entorno',
    development: 'Desarrollo',
    production: 'Producción',
    logout: 'Cerrar Sesión',
    language: 'Idioma',
    group_balance_over_time: 'Agrupar balances a lo largo del tiempo',
    next: 'Siguiente',
    previous: 'Anterior',
    count: 'Cantidad',
    no_locations_found: 'No se encontraron ubicaciones',
    noLocationsFound: 'No se Encontraron Ubicaciones',
    noLocationsFoundSubtitle: 'No hay ubicaciones disponibles en este momento.',
    no_asset_types_found: 'No se encontraron tipos de activos',
    date_to: 'Fecha hasta',
    date_from: 'Fecha desde',
    select_a_tracker: 'Seleccionar un Rastreador',

    observation: 'Observación',
    inventory: 'Inventario',
    administrative: 'Administrativo',
    heartbeat: 'Latido',
    out: 'Saliente',
    in: 'Entrante',
    error: 'Error',
    warning: 'Advertencia',
    info: 'Información',
    event_processing: 'Procesamiento de Eventos',
    other: 'Otro',

    change_asset_type: 'Cambiar Tipo de Activo',
    move_selected_assets_to_another_type: 'Seleccione el tipo de activo para mover los {{count}} activos seleccionados a:',

    // Weekly balance widget
    weekly_balance_widget_title: 'Balance Semanal - {{locationName}}',
    balance_over_time_widget_title: 'Balance a lo Largo del Tiempo - {{locationName}}',
    week_one: 'semana',
    week_other: 'semanas',
    select_period_weeks_description: 'Seleccione el número de semanas para mostrar en el gráfico (1-52 semanas)',
    flow: 'Flujo',
    no_data_available: 'No hay datos disponibles',
    asset_type_select_description: 'Seleccione un tipo de activo para filtrar los datos',
    move_tags_to_location_time_minutes: 'Mover Etiquetas a Ubicación Después del Tiempo Sin Ver (minutos)',
    move_tags_location: 'Ubicación de Mover Etiquetas',
    max_time_not_seen_minutes: 'Tiempo Máximo Gateway Fuera de Línea (minutos)',
    alert_emails: 'Correos de Alerta',
    enter_email_address: 'Ingrese dirección de correo electrónico',
    status: 'Estado',
    never_seen: 'Nunca visto',
    your_locations: 'Sus Ubicaciones',
    select_location_to_view_details: 'Seleccione una ubicación para ver sus detalles',
    no_locations_assigned_description: 'No se le ha asignado acceso a ninguna ubicación. Por favor, contacte a su administrador.',
  },
};
